import React, { useMemo } from 'react';
import { Card, CardHeader, CardBody, CardTitle } from 'reactstrap';
import {
  assessmentNamesFromRubricDocument,
  collectRubricAssessmentKeys,
} from 'js/utils/rubric-org-scores';
import Checkbox from 'js/components/Forms/Checkbox';
import styles from './InheritedBreakouts.module.scss';

function groupBreakoutsByAssessment(breakouts, nameByKey) {
  const groups = new Map();
  for (const breakout of breakouts) {
    const key = breakout.assessment_key;
    if (!groups.has(key)) {
      groups.set(key, {
        assessmentKey: key,
        label: nameByKey[key] || key,
        breakouts: [],
      });
    }
    groups.get(key).breakouts.push(breakout);
  }
  return [...groups.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
}

function InheritedBreakouts({
  document,
  breakouts = [],
  loading = false,
  excludedBreakoutIds = [],
  onChange,
  disabled = false,
}) {
  const assessmentKeys = useMemo(
    () => collectRubricAssessmentKeys(document),
    [document]
  );
  const nameByKey = useMemo(
    () => assessmentNamesFromRubricDocument(document),
    [document]
  );
  const excluded = useMemo(
    () => new Set((excludedBreakoutIds ?? []).map(String)),
    [excludedBreakoutIds]
  );

  const groups = useMemo(
    () => groupBreakoutsByAssessment(breakouts, nameByKey),
    [breakouts, nameByKey]
  );

  const handleToggle = (breakoutId, included) => {
    const id = String(breakoutId);
    const next = new Set(excluded);
    if (included) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange?.([...next]);
  };

  if (assessmentKeys.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle tag="h5">Breakouts</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-muted">
            Breakouts are inherited from assessments linked to this rubric. No
            breakouts have been created yet.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle tag="h5">Breakouts</CardTitle>
        </CardHeader>
        <CardBody></CardBody>
      </Card>
    );
  }

  if (breakouts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle tag="h5">Breakouts</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-muted">
            Breakouts are inherited from assessments linked to this rubric. No
            breakouts have been created yet.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          Breakouts
        </CardTitle>
        <small>
          Breakouts are inherited from linked assessments. Choose which are
          enabled for this rubric.
        </small>
      </CardHeader>
      <CardBody>
        <div className={styles.groupsGrid}>
          {groups.map((group) => (
            <div key={group.assessmentKey} className={styles.group}>
              <div className={styles.groupLabel}>{group.label}</div>
              <div className={styles.breakoutList}>
                {group.breakouts.map((breakout) => {
                  const id = String(breakout.id);
                  const checked = !excluded.has(id);
                  return (
                    <div key={id} className={styles.checkboxItem}>
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => handleToggle(id, e.target.checked)}
                      >
                        {breakout.name}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

export default InheritedBreakouts;
